(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['underscore', 'moment', 'app/applicationUtils',
            'app/launcherApplicationInterface', 'app/wizard/eregWorkflowConfigService',
            'app/userLog/UserLogEntryModel'], factory);
  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    module.exports = factory(require('underscore'), require('moment'), require('./../applicationUtils'),
                             require('./../launcherApplicationInterface'), require('./eregWorkflowConfigService'),
                             require('./../userLog/UserLogEntryModel'));
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function(_, moment, applicationUtils,
                 launcherAppInterface, eregWorkflowConfigService,
                 UserLogEntryModel) {
  'use strict';

  var REDIRECT_TO_VCC = 'REDIRECT_TO_VCC';
  var VCC_HASH_TO_TORSO_HASH_SUFFIX = {
    appointmentPreRegDemographics: '/profile',
    appointmentPreRegInsurance: '/insurance',
    appointmentPreRegBillDetails: '/bill',
    appointmentPreRegPayment: '/payment',
    appointmentPreRegQuestionnaires: '/questionnaire',
    appointmentPreRegForms: '/form',
    appointmentDetail: ''
  };
  var EXPRESS_CHECK_IN_TO_TORSO_HASH_SUFFIX = {
    express: '/expressCheckin',
    queued: '/queued'
  };
  var NAVIGATION_DIRECTIONS = {
    BACK: 'back',
    FORWARD: 'forward'
  };

  var MULTI_APPOINTMENT_DATA_KEY = 'multiRegAppointmentIds';

  /**
   * Utility methods for interacting with the wizard.  This is a utils class so each method does not reference 'this'.
   *
   * @class wizardUtils
   * @static
   */
  return {
    /**
     * @property REDIRECT_TO_VCC {String} specific hash value that indicates a redirect to VCC instead of Torso.
     */
    REDIRECT_TO_VCC: REDIRECT_TO_VCC,

    /**
     * @property APPOINTMENT_REG_DAY_FORMAT {String} the moment parsing/formatting format to use for the appointment registeration day.
     */
    APPOINTMENT_REG_DAY_FORMAT: 'YYYY-MM-DD',

    NAVIGATION_DIRECTIONS: NAVIGATION_DIRECTIONS,

    /**
     * @method getPageUrl
     * @param options {Object}
     *   @param options.hash {String} the hash of the page to get the url for.
     *   @param options.patientVecnaId {String} the vecna id of the patient to get the url for.
     *   @param options.appointmentExternalId {String} the external id of the appointment to get the url for.
     *   @param options.stepAppointmentExternalId {String} to use to generate an appointment specific url.
     *   @param options.regDate {String} the date of the appointments to get the url for.
     *   @param options.name {String} the name parameter to use in a vcc url.
     *   @param options.navigationDirection {String} the direction of navigation for this url.
     * @returns {String} the url of the page to use, or empty string if hash is undefined
     */
    getPageUrl: function(options) {
      var newPageHash = this.getPageHash(options);
      if (newPageHash === '') {
        return '';
      } else if (newPageHash === REDIRECT_TO_VCC) {
        return this.getVccPageUrl(options);
      } else {
        return applicationUtils.currentApplication.fullUrlPath + '#' + newPageHash;
      }
    },

    /**
     * @method getVccPageUrl
     * @param options {Object}
     *   @param options.hash {String} the hash of the page to get the url for.
     *   @param options.appointmentExternalId {String} the external id of the appointment to get the url for.
     *   @param options.regDate {String} the date of the appointments to get the url for.
     *   @param options.name {String} the name parameter to use in a vcc url.
     * @return {String} the url of the VCC step of the wizard corresponding to the given hash.
     */
    getVccPageUrl: function(options) {
      var regIdParam = options.appointmentExternalId ? '/id/' + options.appointmentExternalId : '';
      var regDateParam = options.regDate ? '/regDate/' + options.regDate : '';
      var regNameParam = options.name ? '/name/' + options.name : '';

      var regIdentifier = '';
      if (options.hash === 'appointmentPreRegInsuranceEdit') {
        regIdentifier = regDateParam + regIdParam + regNameParam;
      } else if (options.regDate) {
        regIdentifier = regDateParam + regIdParam;
      } else {
        regIdentifier = regIdParam;
      }

      return '/patient-portal/secure/portal.html#' + options.hash + regIdentifier;
    },

    /**
     * @method getPageHash
     * @param options {Object}
     *   @param options.hash {String} the hash of the page to get the url for.
     *   @param options.patientVecnaId {String} the vecna id of the patient to get the url for.
     *   @param options.appointmentExternalId {String} the external id of the appointment to get the url for.
     *   @param options.stepAppointmentExternalId {String} to use to generate an appointment specific url.
     *   @param options.regDate {String} the date of the appointments to get the url for.
     *   @param options.userIsStaff {Boolean} if the user is a staff user instead of the patient.
     *   @param [options.navigationDirection=false] {String|Boolean} the direction of navigation for this url.
     * @returns {String} the hash of the page to use, empty string if the pageHash is undefined and REDIRECT_TO_VCC if
     *                   the hash is for a page still in VCC.
     */
    getPageHash: function(options) {
      if (_.isUndefined(options.hash)) {
        return '';
      }
      var newPageSuffix = EXPRESS_CHECK_IN_TO_TORSO_HASH_SUFFIX[options.hash] || VCC_HASH_TO_TORSO_HASH_SUFFIX[options.hash];
      if (_.isUndefined(newPageSuffix)) {
        return REDIRECT_TO_VCC;
      }
      if (options.regDate && options.stepAppointmentExternalId) {
        newPageSuffix += '/' + options.stepAppointmentExternalId;
      }
      var navigationDirectionQueryParameters = this.getNavigationDirectionQueryParameters(options.navigationDirection);
      var regScopeUrlFragment = this.getRegBaseHash(options);
      return regScopeUrlFragment + newPageSuffix + navigationDirectionQueryParameters;
    },

    /**
     * Get the start of the hash that includes the patient and appointment ext id or regDate.
     * @method getRegBaseHash
     * @param wizardUrlDetails {Object}
     *   @param wizardUrlDetails.patientVecnaId {String} the patient
     *   @param wizardUrlDetails.appointmentExternalId {String} to use instead if regDate isn't set.
     *   @param wizardUrlDetails.regDate {String} to use.  If set, then the appt ext id is not used.
     *   @param wizardUrlDetails.userIsStaff {Boolean} true if the user is a staff user (and therefore should use the staff versions of the urls).
     * @return {String} the beginning of the hash (w/o the #).
     */
    getRegBaseHash: function(wizardUrlDetails) {
      if (!wizardUrlDetails.patientVecnaId) {
        throw 'PatientVecnaId is required to get a registration hash';
      }

      var regPathSuffix = '';
      if (wizardUrlDetails.regDate) {
        regPathSuffix = '/appointments/' + wizardUrlDetails.regDate;
      } else if (wizardUrlDetails.appointmentExternalId) {
        regPathSuffix = '/appointment/' + wizardUrlDetails.appointmentExternalId;
      }
      var userTypePrefix = wizardUrlDetails.userIsStaff ? 'staff/patient/' : 'patient/';
      return userTypePrefix + wizardUrlDetails.patientVecnaId + regPathSuffix;
    },

    /**
     * Get the root of the hash for reg including the appointment / appointments part of it.
     * This is baseHash + /reg for patient and baseHash + /workflow for staff.
     * @method generateRootRegHash
     * @param wizardUrlDetails {Object}
     *   @param wizardUrlDetails.patientVecnaId {String} the vecna id of the patient that this url is generated for.
     *   @param wizardUrlDetails.appointmentExternalId {String} the external id of the appointment when doing single appointment reg mode.
     *   @param wizardUrlDetails.regDate {String} the reg date of the appointments when doing multi appointments reg mode.
     *   @param wizardUrlDetails.userIsStaff {Boolean} true if the user is a staff user (and therefore should use the staff versions of the urls).
     * @return {String} the hash to use for
     */
    generateRootRegHash: function(wizardUrlDetails) {
      var baseReg = this.getRegBaseHash(wizardUrlDetails);
      var regSuffix = wizardUrlDetails.userIsStaff ? '/workflow' : '/reg';
      return baseReg + regSuffix;
    },

    /**
     * Get the base of the url including the appointment / appointments part of it.
     * @method generateBaseRegUrl
     * @param wizardUrlDetails {Object}
     *   @param wizardUrlDetails.patientVecnaId {String} the vecna id of the patient that this url is generated for.
     *   @param wizardUrlDetails.appointmentExternalId {String} the external id of the appointment when doing single appointment reg mode.
     *   @param wizardUrlDetails.regDate {String} the reg date of the appointments when doing multi appointments reg mode.
     *   @param wizardUrlDetails.userIsStaff {Boolean} true if the user is a staff user (and therefore should use the staff versions of the urls).
     * @return {String} the base part of the url to use for
     */
    generateBaseRegUrl: function(wizardUrlDetails) {
      return applicationUtils.currentApplication.fullUrlPath + '#' + this.getRegBaseHash(wizardUrlDetails);
    },

    /**
     * Get the root of the url for reg including the appointment / appointments part of it.
     * This is baseUrl + /reg for patient and baseUrl + /workflow for staff.
     * @method generateRootRegUrl
     * @param wizardUrlDetails {Object}
     *   @param wizardUrlDetails.patientVecnaId {String} the vecna id of the patient that this url is generated for.
     *   @param wizardUrlDetails.appointmentExternalId {String} the external id of the appointment when doing single appointment reg mode.
     *   @param wizardUrlDetails.regDate {String} the reg date of the appointments when doing multi appointments reg mode.
     *   @param wizardUrlDetails.userIsStaff {Boolean} true if the user is a staff user (and therefore should use the staff versions of the urls).
     * @return {String} the base part of the url to use for
     */
    generateRootRegUrl: function(wizardUrlDetails) {
      return applicationUtils.currentApplication.fullUrlPath + '#' + this.generateRootRegHash(wizardUrlDetails);
    },

    /**
     * @method getNavigationDirectionQueryParameters
     * @param [navigationDirection=false] {String|Boolean} Either 'forward', 'back' or falsy.
     * @return {String} the query parameters to add to wizard navigation urls to preserve wizard direction information.
     */
    getNavigationDirectionQueryParameters: function(navigationDirection) {
      var navigationDirectionQueryParameters = '';
      if (navigationDirection) {
        navigationDirectionQueryParameters = '?navigationDirection=' + navigationDirection;
      }
      return navigationDirectionQueryParameters;
    },

    /**
     * @method getFirstEnabledStepPageHash
     * @param wizardConfiguration {Object} the configuration of the wizard from the server.
     * @param wizardId {Object}
     *   @param wizardId.patientVecnaId {String} the vecna id of the patient to get the steps for.
     *   @param wizardId.appointmentExternalId {String} the external id of the appointment being managed by this wizard config.
     *   @param wizardId.regDate {String} the date that the appointments are being registered for (used in lieu of appt ext id).
     */
    getFirstEnabledStepPageHash: function(wizardConfiguration, wizardId) {
      var disabledSteps = eregWorkflowConfigService.getDisabledWizardSteps({
        patientVecnaId: wizardId.patientVecnaId,
        appointmentExternalId: wizardId.appointmentExternalId,
        regDate: wizardId.regDate
      });

      var firstEnabledStep = _.find(wizardConfiguration.wizardSteps, function(step) {
        var enabledStep = !disabledSteps[step.name];
        if (launcherAppInterface.isAtHospital() || applicationUtils.currentApplication === applicationUtils.APPLICATIONS.EREG) {
          return enabledStep;
        }
        var completedStep = step.status === 'DONE';
        return enabledStep && !completedStep;
      });

      return this.getPageHash({
        hash: firstEnabledStep.hash,
        patientVecnaId: wizardId.patientVecnaId,
        appointmentExternalId: wizardId.appointmentExternalId,
        regDate: wizardId.regDate,
        navigationDirection: NAVIGATION_DIRECTIONS.FORWARD
      });
    },

    /**
     * @method getFirstEnabledStepFromWizardData
     * @param wizardData {Object} the survey/form step configuration of the wizard from the server.
     * @param disabledSteps the steps that have been marked as disabled.
     */
    getFirstEnabledStepFromWizardData: function(wizardData, disabledSteps) {
      return _.find(wizardData.wizardSteps, function(step) {
        var enabledStep = !disabledSteps[step.name];
        if (launcherAppInterface.isAtHospital() || applicationUtils.currentApplication === applicationUtils.APPLICATIONS.EREG) {
          return enabledStep;
        }
        var completedStep = step.status === 'DONE';
        return enabledStep && !completedStep;
      });
    },

    /**
     * Navigates page to a different url
     *
     * @method navigateToLink
     * @param url {String} of link
     */
    navigateToLink: function(url) {
      window.location.href = url;
    },

    /**
     * Determine if the appointment and appointment type combination are pre-regiable.
     * @method isAppointmentPreRegisterable
     * @param appointment {Object} the external appointment used to check whether it is already pre-registered, checked in, or canceled.
     *   @param appointment.time {Number} ms since epoch representing the time & date of the appointment.
     *   @param appointment.preregistered {Boolean} whether the appointment is already pre-registered.
     *   @param appointment.checkedIn {Boolean} whether the appointment is already checked-in.
     *   @param appointment.cancelled {Boolean} whether the appointment has been cancelled.
     * @param appointmentType {AppointmentTypeModel} to check effective enabled settings along with effective pre-reg period.
     * @return {boolean} true if the
     */
    isAppointmentPreRegisterable: function(appointment, appointmentType) {
      if (!appointmentType) {
        return false;
      }
      if (launcherAppInterface.isAtHospital()) {
        return appointmentType.getEffectiveCheckInEnabled();
      }
      var isAppointmentPreRegisterable = !appointment.preregistered && !appointment.checkedIn && !appointment.cancelled &&
        appointmentType.getEffectivePreRegEnabled();
      var firstDayToPreReg = moment(appointment.time).subtract(appointmentType.getEffectiveFirstDayToPreReg(), 'days');
      var lastDayToPreReg = moment(appointment.time).subtract(appointmentType.getEffectiveLastDayToPreReg(), 'days');
      return isAppointmentPreRegisterable && moment().isSameOrAfter(firstDayToPreReg) && moment().isSameOrBefore(lastDayToPreReg);
    },

    /**
     * Private getter that gets the data from the local storage and converts it into an object.
     * @method _getMultiAppointmentSessionData
     * @return {Object} an object representing the map from reg date to collection of appointment ids.
     * @private
     */
    _getMultiAppointmentSessionData: function() {
      var multiAppointmentSessionData = localStorage.getItem(MULTI_APPOINTMENT_DATA_KEY) || '{}';
      return JSON.parse(multiAppointmentSessionData);
    },

    /**
     * Private setter that sets the data to the local storage as a string.
     * @method _saveMultiAppointmentSessionData
     * @param multiAppointmentData {Object} the data to save.
     * @private
     */
    _saveMultiAppointmentSessionData: function(multiAppointmentData) {
      multiAppointmentData = this._removeOldMultiAppointmentData(multiAppointmentData);
      localStorage.setItem(MULTI_APPOINTMENT_DATA_KEY, JSON.stringify(multiAppointmentData));
    },

    /**
     * Clears out the old dates from the multi-appt data so they do not fill up all the disk space.
     * @param multiAppointmentData {Object}
     * @return {Object} the trimmed multi-appointment data.
     * @private
     */
    _removeOldMultiAppointmentData: function(multiAppointmentData) {
      multiAppointmentData = multiAppointmentData || {};
      // Use yesterday to give some buffer around things like midnight.
      var yesterday = moment()
        .startOf('date')
        .subtract(1, 'days');
      // Remove any date from the data that is before the beginning of yesterday.
      return _.omit(multiAppointmentData, function(data, regDate) {
        return moment(regDate).isBefore(yesterday);
      });
    },

    /**
     * Generic method to safely get the property from the multi appointment session data for the given reg date.
     *
     * @method _getMultiAppointmentSessionProperty
     * @param regDate {String} that is used to key the external ids.
     * @param propertyName {String} the name of the property to return.
     * @return {*} the value of the property
     * @private
     */
    _getMultiAppointmentSessionProperty: function(regDate, propertyName) {
      if (!regDate) {
        return null;
      }
      var multiAppointmentSessionData = this._getMultiAppointmentSessionData();
      var multiAppointmentSessionDataForDate = multiAppointmentSessionData[regDate];
      if (!multiAppointmentSessionDataForDate || !multiAppointmentSessionDataForDate[propertyName]) {
        throw 'No ' + propertyName + ' value for this reg date.';
      }
      return multiAppointmentSessionDataForDate[propertyName];
    },

    /**
     * Get the appointment external ids that are saved to the given property.
     * @method _getMultiAppointmentExternalIdsProperty
     * @param appointmentExternalId {String} the external id of the appointment (if not using regDate).
     * @param regDate {String} the date of the registration (if not registering a single appointment).
     * @param propertyName {String} the name of the property to retrieve ('selected' or 'filtered').
     * @return {String[]} the appointment external ids for the given state.
     * @private
     */
    _getMultiAppointmentExternalIdsProperty: function(appointmentExternalId, regDate, propertyName) {
      if (regDate) {
        return this._getMultiAppointmentSessionProperty(regDate, propertyName);
      } else if (appointmentExternalId) {
        return [ appointmentExternalId ];
      } else {
        throw 'Appointment external id or reg date is required to use the registration wizard.';
      }
    },

    /**
     * Save or overwrite the existing property value for the given reg date.
     * @method _saveMultiAppointmentSessionProperty
     * @param regDate that is used to key the property.
     * @param propertyName the name of the property to save.
     * @param propertyValue the property value to save.
     */
    _saveMultiAppointmentSessionProperty: function(regDate, propertyName, propertyValue) {
      if (!regDate) {
        return;
      }

      var multiAppointmentSessionData = this._getMultiAppointmentSessionData();
      var regDateSessionData = multiAppointmentSessionData[regDate] || {};
      if (propertyName === 'selected') {
        // Clear session data when selecting new items.
        regDateSessionData = {};
      }
      regDateSessionData[propertyName] = propertyValue;

      multiAppointmentSessionData[regDate] = regDateSessionData;
      this._saveMultiAppointmentSessionData(multiAppointmentSessionData);
    },

    /**
     * These are the ids identified by the patient that they want to reg for.
     * These are then used by the wizard to create the root wizard flow.  During that process any hard-stopped appointments
     * are weeded out and only the appts that are not hard-stopped are presented for registration.  Those external ids
     * are managed by the other par of methods w/o Selected in their name:
     *   getPreRegWizardAppointmentExternalIds
     *   savePreRegWizardAppointmentExternalIds
     *
     * @method getPreRegWizardAppointmentExternalIds
     * @param appointmentExternalId {String} to use instead of reg date to determine what ids to use.
     * @param regDate {String} that is used to key the external ids.
     * @return {String[]} the array of appointment external ids to use.
     */
    getSelectedPreRegWizardAppointmentExternalIds: function(appointmentExternalId, regDate) {
      return this._getMultiAppointmentExternalIdsProperty(appointmentExternalId, regDate, 'selected');
    },

    /**
     * Save or overwrite the existing appointment external ids that were selected for this date.
     * @method savePreRegWizardAppointmentExternalIds
     * @param regDate {String} that is used to key the external ids.
     * @param appointmentExternalIds {String[]} to associate with this reg date.
     */
    saveSelectedPreRegWizardAppointmentExternalIds: function(regDate, appointmentExternalIds) {
      this._saveMultiAppointmentSessionProperty(regDate, 'selected', appointmentExternalIds);
    },

    /**
     * Get the appt external ids to use while the wizard is being processed (the hard-stopped appts are filtered out).
     * @method getPreRegWizardAppointmentExternalIds
     * @param appointmentExternalId {String} to use instead of reg date to determine what ids to use.
     * @param regDate {String} that is used to key the external ids.
     * @return {String[]} the array of appointment external ids to use.
     */
    getPreRegWizardAppointmentExternalIds: function(appointmentExternalId, regDate) {
      return this._getMultiAppointmentExternalIdsProperty(appointmentExternalId, regDate, 'filtered');
    },

    /**
     * Save or overwrite the existing appointment external ids that were filtered to be processed for this date.
     * @method savePreRegWizardAppointmentExternalIds
     * @param regDate {String} that is used to key the external ids.
     * @param appointmentExternalIds {String[]} to associate with this reg date.
     */
    savePreRegWizardAppointmentExternalIds: function(regDate, appointmentExternalIds) {
      this._saveMultiAppointmentSessionProperty(regDate, 'filtered', appointmentExternalIds);
    },

    /**
     * Log the fact that the wizard is starting to the server's timing api.
     * @method logWizardStart
     * @param appointmentExternalIds {String[]} appointment ids to log.  Could be one or many.
     */
    logWizardStart: function(appointmentExternalIds) {
      if (_.size(appointmentExternalIds) === 0) {
        return;
      }

      var appointmentWorkObjects = _.map(appointmentExternalIds, function(appointmentExternalId) {
        return {
          workObjectType: 'APPOINTMENT',
          workObjectId: appointmentExternalId
        };
      });
      var actionType = appointmentExternalIds.length > 1 ? 'MULTI_APPOINTMENT' : '';
      var actionSuffix = launcherAppInterface.isAtHospital() ? 'EREG' : 'PREREG';
      var action = 'START_' + actionType + actionSuffix;

      var regStartedLogEntry = new UserLogEntryModel({
        action: action,
        workObjects: appointmentWorkObjects
      });
      regStartedLogEntry.save();
    },

    /**
     * Cleans wizard steps for easier processing
     * First, removes disabled top-level steps, then replaces wizard steps by their child wizard steps
     *
     * @method generateSequentialWizardSteps
     * @param wizardSteps {Object[]} to clean
     * @param wizardId {Object}
     *   @param wizardId.patientVecnaId {String} the patient id to use for this wizard.
     *   @param wizardId.appointmentExternalId {String} the external id to use for this wizard.
     *   @param wizardId.regDate {String} the registration date to use for this wizard.
     * @return {Object[]} cleaned wizard
     * @private
     */
    generateSequentialWizardSteps: function(wizardSteps, wizardId) {
      if (!wizardSteps) {
        return null;
      }
      var cleanedWizard = [];
      _.chain(wizardSteps)
        .filter(function(wizardStep) { // remove disabled top-level steps
          return eregWorkflowConfigService.isEnabledStep({
            topLevelStepName: wizardStep.name,
            patientVecnaId: wizardId.patientVecnaId,
            appointmentExternalId: wizardId.appointmentExternalId,
            regDate: wizardId.regDate
          });
        }, this)
        .each(function(wizardStep) { // replace wizards by their child wizard
          var hasChildWizard = !!wizardStep.children;
          if (hasChildWizard) {
            var childWizard = wizardStep.children[0];
            _.each(childWizard.wizardSteps, function(childWizardStep) {
              cleanedWizard.push($.extend(true, {}, childWizardStep));
            });
          } else {
            cleanedWizard.push($.extend(true, {}, wizardStep));
          }
        });
      return cleanedWizard;
    }
  };
}));
